import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle, Warning, Clock, CalendarBlank } from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { ARTICLES } from "../data/articles";
import { CHAPTERS, IMAGES } from "../data/content";
import { scrollToId } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

export default function Article() {
  const { slug } = useParams();
  const index = ARTICLES.findIndex((a) => a.slug === slug);
  const article = ARTICLES[index];
  const chapter = CHAPTERS.find((c) => c.number === article?.number);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | The Timeshare Edit`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && chapter) meta.setAttribute("content", chapter.lede);
    }
    return () => {
      document.title = "The Timeshare Edit — Vacation Ownership Guidance";
    };
  }, [article, chapter]);

  useEffect(() => {
    if (!article || !chapter) return;
    const origin = window.location.origin;
    const wordCount = article.sections.reduce(
      (n, s) =>
        n +
        s.paragraphs.join(" ").split(/\s+/).length +
        (s.list ? s.list.join(" ").split(/\s+/).length : 0),
      0
    );
    const minutes = parseInt(article.readTime, 10) || 7;
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: chapter.lede,
      image: article.image ? [IMAGES[article.image]] : undefined,
      datePublished: "2026-08-05",
      dateModified: "2026-08-07",
      wordCount,
      timeRequired: `PT${minutes}M`,
      inLanguage: "en",
      articleSection: `Chapter ${article.number} — The Manifesto`,
      mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/guides/${article.slug}` },
      author: { "@type": "Organization", name: "The Timeshare Edit", url: origin },
      publisher: { "@type": "Organization", name: "The Timeshare Edit", url: origin },
    };
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "The Chapters", item: `${origin}/#guides` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${origin}/guides/${article.slug}` },
      ],
    };
    const scripts = [articleSchema, breadcrumbSchema].map((data) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-article-schema", "true");
      el.text = JSON.stringify(data);
      document.head.appendChild(el);
      return el;
    });
    return () => scripts.forEach((el) => el.remove());
  }, [article, chapter]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-6 md:px-12 pt-48 pb-32" data-testid="article-not-found">
          <p className="font-serif text-4xl text-primary">Chapter not found</p>
          <Link to="/" data-testid="article-not-found-home-link" className="inline-block mt-6 text-sm font-semibold text-accent border-b-2 border-accent pb-1">
            Back to the guides
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const prev = ARTICLES[index - 1];
  const next = ARTICLES[index + 1];
  const takeaways = chapter?.points || chapter?.checklist || [];

  return (
    <div className="min-h-screen bg-background" data-testid="article-page">
      <Navbar />
      <main className="pt-[72px]">
        <header className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-28">
          <Reveal>
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); window.location.href = "/#guides"; }}
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
            >
              <ArrowLeft size={16} weight="bold" /> All chapters
            </Link>
            <div className="mt-12 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8 relative">
                <span aria-hidden className="font-serif text-outline select-none pointer-events-none absolute -top-[10vw] -left-2 text-[22vw] lg:text-[11vw] leading-none">
                  {article.number}
                </span>
                <p className="relative uppercase tracking-[0.25em] text-xs font-bold text-accent font-sans" data-testid="article-eyebrow">
                  Chapter {article.number} · The Manifesto
                </p>
                <h1 className="relative font-serif text-primary tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mt-6" data-testid="article-title">
                  {article.title}
                </h1>
                {chapter && (
                  <p className="relative mt-8 text-lg text-foreground/70 max-w-2xl leading-relaxed" data-testid="article-lede">
                    {chapter.lede}
                  </p>
                )}
                <div className="relative mt-8 flex items-center gap-6 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground" data-testid="article-meta">
                  <span className="flex items-center gap-2"><Clock size={15} weight="bold" /> {article.readTime}</span>
                  <span className="flex items-center gap-2"><CalendarBlank size={15} weight="bold" /> Updated {article.updated}</span>
                </div>
              </div>
              {article.image && (
                <motion.div
                  initial={{ clipPath: "inset(0 0 100% 0)" }}
                  animate={{ clipPath: "inset(0 0 0% 0)" }}
                  transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
                  className="lg:col-span-4 overflow-hidden rounded-sm border border-border aspect-[4/3]"
                >
                  <img src={IMAGES[article.image]} alt={article.title} className="w-full h-full object-cover" data-testid="article-image" />
                </motion.div>
              )}
            </div>
          </Reveal>
        </header>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-20 md:mt-28 grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-7 lg:col-start-2" data-testid="article-body">
            {article.sections.map((s, i) => (
              <Reveal key={i} className="mb-16">
                <h2 className="font-serif text-primary tracking-tight text-2xl sm:text-3xl leading-snug">
                  <span className="text-accent mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                {s.paragraphs.map((p, j) => (
                  <p key={j} className="mt-6 text-foreground/75 leading-[1.85]">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-8 space-y-3">
                    {s.list.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 bg-card border border-border rounded-sm px-5 py-4 text-sm md:text-base font-medium text-foreground/80">
                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            ))}
          </article>

          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-8">
              <Reveal delay={0.1} className="bg-secondary/50 border border-border rounded-sm p-7" data-testid="article-takeaways">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Key takeaways</p>
                <ul className="mt-5 space-y-4">
                  {takeaways.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-medium text-foreground/75 leading-relaxed">
                      {article.number === "04" ? (
                        <Warning size={17} weight="fill" className="text-accent shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle size={17} weight="fill" className="text-primary shrink-0 mt-0.5" />
                      )}
                      {t}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.2} className="bg-primary text-primary-foreground rounded-sm p-7" data-testid="article-cta-card">
                <p className="font-serif text-2xl leading-snug">Still weighing it up?</p>
                <p className="mt-3 text-sm text-primary-foreground/70 leading-relaxed">
                  Talk your situation through with a real person — free, and with no pressure either way.
                </p>
                <Link
                  to="/"
                  onClick={(e) => { e.preventDefault(); window.location.href = "/#consult"; }}
                  data-testid="article-cta-consult"
                  className="group mt-6 inline-flex items-center gap-2 bg-accent text-accent-foreground text-sm font-semibold px-6 py-3 rounded-full hover:bg-background hover:text-primary transition-colors duration-300"
                >
                  Request a consultation
                  <ArrowRight size={15} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </aside>
        </div>

        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24 mb-4" data-testid="article-pagination">
          <div className="grid md:grid-cols-2 border-t border-border">
            {prev ? (
              <Link to={`/guides/${prev.slug}`} data-testid="article-prev-link" className="group py-10 md:pr-12 border-b md:border-b-0 md:border-r border-border">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
                  <ArrowLeft size={14} weight="bold" className="transition-transform duration-300 group-hover:-translate-x-1" /> Previous chapter
                </p>
                <p className="font-serif text-xl md:text-2xl text-primary mt-3 group-hover:text-accent transition-colors duration-300">{prev.title}</p>
              </Link>
            ) : <div className="hidden md:block border-r border-border" />}
            {next ? (
              <Link to={`/guides/${next.slug}`} data-testid="article-next-link" className="group py-10 md:pl-12 md:text-right">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2 md:justify-end">
                  Next chapter <ArrowRight size={14} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </p>
                <p className="font-serif text-xl md:text-2xl text-primary mt-3 group-hover:text-accent transition-colors duration-300">{next.title}</p>
              </Link>
            ) : (
              <Link to="/" onClick={(e) => { e.preventDefault(); window.location.href = "/#companies"; }} data-testid="article-next-link" className="group py-10 md:pl-12 md:text-right">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2 md:justify-end">
                  Continue <ArrowRight size={14} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </p>
                <p className="font-serif text-xl md:text-2xl text-primary mt-3 group-hover:text-accent transition-colors duration-300">Browse the company directory</p>
              </Link>
            )}
          </div>
        </nav>
      </main>
      <Footer />
    </div>
  );
}
